import React, { useState, useEffect } from 'react';
import {
    RefreshCw,
    Building,
    AlertCircle,
    Clock,
    CheckCircle,
    Globe,
} from 'lucide-react';

const ZohoBooksOverview = ({ onOrganizationSwitch }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showOrgSelector, setShowOrgSelector] = useState(false);

    // Organization and token management
    const [currentOrganization, setCurrentOrganization] = useState(null);
    const [availableOrganizations, setAvailableOrganizations] = useState([]);
    const [tokenStatus, setTokenStatus] = useState({
        has_token: false,
        expires_in_minutes: 0,
        expires_at: null
    });

    // State for different data types
    const [data, setData] = useState({
        organizations: []
    });

    const API_BASE_URL = 'https://api.zapbooksai.subraps.com/api';

    // Fetch token status
    const fetchTokenStatus = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/token/status`);
            const result = await response.json();

            if (result.success) {
                setTokenStatus(result.data);
                const newOrganization = result.data.current_organization;
                setCurrentOrganization(newOrganization);

                // UPDATED: Notify parent component about organization change
                if (onOrganizationSwitch && newOrganization) {
                    onOrganizationSwitch(newOrganization);
                }
            }
        } catch (err) {
            console.error('Error fetching token status:', err);
        }
    };

    // Fetch available organizations
    const fetchOrganizations = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/organizations`);
            const result = await response.json();

            if (result.success) {
                const orgs = result.data.organizations || [];
                setAvailableOrganizations(orgs);
                setData(prev => ({ ...prev, organizations: orgs }));
                return orgs;
            } else {
                throw new Error(result.message || 'Failed to fetch organizations');
            }
        } catch (err) {
            console.error('Error fetching organizations:', err);
            throw err;
        }
    };

    // Switch organization
    const switchOrganization = async (organizationId) => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/organization/switch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ organization_id: organizationId }),
            });

            const result = await response.json();

            if (result.success) {
                const newOrganization = result.data.organization;
                setCurrentOrganization(newOrganization);
                setSuccess(`Switched to ${newOrganization.name}`);
                setShowOrgSelector(false);

                // UPDATED: Notify parent component about organization change
                if (onOrganizationSwitch) {
                    onOrganizationSwitch(newOrganization);
                }
            } else {
                throw new Error(result.message || 'Failed to switch organization');
            }
        } catch (err) {
            setError('Failed to switch organization: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Refresh token manually
    const refreshToken = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/token/refresh`, {
                method: 'POST',
            });

            const result = await response.json();

            if (result.success) {
                setSuccess('Token refreshed successfully');
                await fetchTokenStatus();
            } else {
                throw new Error(result.message || 'Failed to refresh token');
            }
        } catch (err) {
            setError('Failed to refresh token: ' + err.message);
        }
    };

    // Clear messages after 5 seconds
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    // Fetch initial data and token status
    useEffect(() => {
        fetchTokenStatus();
    }, []);

    // Auto-refresh token status every minute
    useEffect(() => {
        const interval = setInterval(fetchTokenStatus, 60000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                                <Building className="w-8 h-8 text-blue-600" />
                                Zoho Books Overview
                            </h2>
                            <p className="text-gray-600 mt-1">Complete overview of your Zoho Books organization</p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowOrgSelector(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                            >
                                <Globe className="w-4 h-4" />
                                Switch Org
                            </button>
                            <button
                                onClick={refreshToken}
                                className="flex items-center gap-2 px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                                title="Refresh Access Token"
                            >
                                <Clock className="w-4 h-4" />
                                Refresh Token
                            </button>
                        </div>
                    </div>

                    {/* Current Organization & Token Status */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                        {/* Current Organization */}
                        {currentOrganization && (
                            <div className="bg-blue-50 rounded-lg p-4">
                                <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                                    <Building className="w-5 h-5" />
                                    Current Organization
                                </h3>
                                <div className="mt-2 space-y-1 text-sm">
                                    <p><span className="font-medium">Name:</span> {currentOrganization.name}</p>
                                    <p><span className="font-medium">ID:</span> {currentOrganization.organization_id}</p>
                                    <p><span className="font-medium">Currency:</span> {currentOrganization.currency_code}</p>
                                    <p><span className="font-medium">Time Zone:</span> {currentOrganization.time_zone}</p>
                                </div>
                            </div>
                        )}

                        {/* Token Status */}
                        <div className={`rounded-lg p-4 ${tokenStatus.expires_in_minutes > 30
                            ? 'bg-green-50'
                            : tokenStatus.expires_in_minutes > 10
                                ? 'bg-yellow-50'
                                : 'bg-red-50'
                            }`}>
                            <h3 className={`font-semibold flex items-center gap-2 ${tokenStatus.expires_in_minutes > 30
                                ? 'text-green-900'
                                : tokenStatus.expires_in_minutes > 10
                                    ? 'text-yellow-900'
                                    : 'text-red-900'
                                }`}>
                                {tokenStatus.has_token ? (
                                    <CheckCircle className="w-5 h-5" />
                                ) : (
                                    <AlertCircle className="w-5 h-5" />
                                )}
                                Token Status
                            </h3>
                            <div className="mt-2 space-y-1 text-sm">
                                <p><span className="font-medium">Status:</span> {tokenStatus.has_token ? 'Active' : 'Inactive'}</p>
                                <p><span className="font-medium">Expires in:</span> {tokenStatus.expires_in_minutes || 0} minutes</p>
                                {tokenStatus.expires_at && (
                                    <p><span className="font-medium">Expires at:</span> {new Date(tokenStatus.expires_at).toLocaleString()}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Messages */}
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {success && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md">
                    {success}
                </div>
            )}

            {/* Organization Selector Modal */}
            {showOrgSelector && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                    <Globe className="w-6 h-6 text-blue-600" />
                                    Switch Organization
                                </h3>
                                <button
                                    onClick={() => setShowOrgSelector(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    ✕
                                </button>
                            </div>
                            {currentOrganization && (
                                <p className="text-sm text-gray-600 mt-2">
                                    Currently using: <span className="font-medium text-blue-600">{currentOrganization.name}</span>
                                </p>
                            )}
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {loading ? (
                                <div className="flex items-center justify-center py-8">
                                    <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                                    <span className="ml-2 text-gray-600">Loading organizations...</span>
                                </div>
                            ) : availableOrganizations.length === 0 ? (
                                <div className="text-center py-8">
                                    <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-500 mb-2">No organizations available</p>
                                    <button
                                        onClick={fetchOrganizations}
                                        className="text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                        Try refreshing organizations
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <div className="text-sm text-gray-600 mb-4">
                                        Select an organization to switch to:
                                    </div>
                                    {availableOrganizations.map((org) => (
                                        <div
                                            key={org.organization_id}
                                            className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${currentOrganization?.organization_id === org.organization_id
                                                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                                                }`}
                                            onClick={() => {
                                                if (currentOrganization?.organization_id !== org.organization_id) {
                                                    switchOrganization(org.organization_id);
                                                }
                                            }}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3">
                                                        <Building className="w-5 h-5 text-gray-400" />
                                                        <div>
                                                            <h4 className="font-medium text-gray-900">{org.name}</h4>
                                                            <p className="text-sm text-gray-500">ID: {org.organization_id}</p>
                                                        </div>
                                                    </div>
                                                    <div className="mt-2 flex gap-4 text-xs text-gray-500">
                                                        <span>💰 {org.currency_code}</span>
                                                        <span>🌍 {org.time_zone}</span>
                                                        {org.language_code && <span>🗣️ {org.language_code}</span>}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {currentOrganization?.organization_id === org.organization_id ? (
                                                        <div className="flex items-center gap-2">
                                                            <CheckCircle className="w-5 h-5 text-green-600" />
                                                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                                                                Current
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <div className="text-blue-600 hover:text-blue-800">
                                                            <span className="text-xs bg-blue-100 hover:bg-blue-200 px-3 py-1 rounded-full font-medium transition-colors">
                                                                Switch
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="p-6 border-t border-gray-200 bg-gray-50">
                            <div className="flex justify-between items-center">
                                <div className="text-sm text-gray-600">
                                    {availableOrganizations.length > 0 && (
                                        <span>{availableOrganizations.length} organization{availableOrganizations.length !== 1 ? 's' : ''} available</span>
                                    )}
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={fetchOrganizations}
                                        disabled={loading}
                                        className="flex items-center gap-2 px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                        Refresh
                                    </button>
                                    <button
                                        onClick={() => setShowOrgSelector(false)}
                                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ZohoBooksOverview;